import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { SidebarProvider, useSidebar } from "../../components/ui/Sidebar";
import Sidebar from "../../components/ui/Sidebar";
import toast from "react-hot-toast";

import { motion } from "framer-motion";
import {
  TrendingUp,
  TrendingDown,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Shield,
  Award, // Replaced 'Certificate' with valid 'Award' icon
  Plus,
  Upload,
  Zap,
  DollarSign,
  Activity,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import { useGetCompany } from "pages/company-settings/useGetCompany";
import { useGetDevices } from "pages/device-settings/useGetDevices";
import { useGetInvoices } from "pages/invoice-management-hub/useGetInvoices";
import { useGetLoggedInUser } from "./useGetLoggedInUser";
import { getPayments } from "api/payment.api";
import { getClients } from "api/client.api";
import { getRefunds } from "api/refund.api";
import {
  getAccountsReceivableAging,
  getProfitAndLoss,
} from "api/accounting.api";
import { QUERY_KEYS } from "config/queryKeys";
import { ROUTES } from "config/routes";
const money = (value) =>
  Number(value || 0).toLocaleString("en-SA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getInvoiceAmount = (invoice) =>
  Number(
    invoice?.invoiceTotals?.payableAmount ??
      invoice?.invoiceTotals?.taxInclusiveAmount ??
      invoice?.amount ??
      0,
  );

const monthKey = (date) =>
  new Date(date).toLocaleString("en-US", {
    month: "short",
  });

const isSameDay = (date, compareDate = new Date()) => {
  if (!date) return false;

  const first = new Date(date);
  const second = new Date(compareDate);

  return (
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate()
  );
};

const getRecentDate = (item) =>
  item.time ||
  item.createdAt ||
  item.updatedAt ||
  item.issuedAt ||
  item.paymentDate ||
  item.refundDate ||
  item.date;

const getList = (value, keys = []) => {
  if (Array.isArray(value)) return value;

  for (const key of keys) {
    if (Array.isArray(value?.[key])) return value[key];
    if (Array.isArray(value?.data?.[key])) return value.data[key];
  }

  if (Array.isArray(value?.data)) return value.data;

  return [];
};

const getValidDate = (...values) => {
  for (const value of values) {
    if (!value) continue;

    const date = new Date(value);

    if (!Number.isNaN(date.getTime())) {
      return date;
    }
  }

  return null;
};

const getDaysLeft = (date) => {
  if (!date) return null;

  return Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
};

const getDeviceCertificateExpiry = (device) =>
  getValidDate(
    device?.productionCertificate?.expiresAt,
    device?.productionCertificate?.validTo,
    device?.certificate?.expiresAt,
    device?.certificate?.validTo,
    device?.certificate?.notAfter,
    device?.certificateExpiresAt,
    device?.certificateExpiryDate,
    device?.certificateValidTo,
    device?.csidExpiresAt,
    device?.pcsidExpiresAt,
  );
const getDashboardInvoiceStatus = (invoice) => {
  const zatcaStatus =
    invoice.clearanceStatus ||
    invoice.reportingStatus ||
    invoice.zatcaStatus ||
    invoice.complianceStatus;

  if (["CLEARED", "REPORTED", "ACCEPTED"].includes(zatcaStatus)) {
    return "CLEARED";
  }

  if (["REJECTED", "FAILED", "ERROR"].includes(zatcaStatus)) {
    return "REJECTED";
  }

  if (invoice.status === "DRAFT") return "DRAFT";
  if (invoice.status === "VOID") return "VOID";
  if (invoice.status === "CLEARED") return "CLEARED";
  if (invoice.status === "REJECTED") return "REJECTED";

  return "PENDING";
};
const getInvoiceStatus = (invoice) => getDashboardInvoiceStatus(invoice);

const getInvoiceActivityDate = (invoice) =>
  invoice.issuedAt_legal ||
  invoice.reportedAt ||
  invoice.createdAt ||
  invoice.issuedAt;

const DashboardContent = () => {
  const { t } = useTranslation("pages");
  const location = useLocation();
  const navigate = useNavigate();
  const { data: company } = useGetCompany();
  const { data: invoices } = useGetInvoices();
  const { data: devices } = useGetDevices();
  const { data: user } = useGetLoggedInUser();

  const { data: paymentsData } = useQuery({
    queryKey: QUERY_KEYS.payments.list(),
    queryFn: () => getPayments({ limit: 100 }),
    staleTime: 1000 * 60 * 5,
  });

  const { data: clientsData } = useQuery({
    queryKey: QUERY_KEYS.clients.list(),
    queryFn: () => getClients({ page: 1, limit: 100 }),
    staleTime: 1000 * 60 * 5,
  });

  const { data: refundsData } = useQuery({
    queryKey: QUERY_KEYS.refunds.list(),
    queryFn: () => getRefunds({ limit: 100 }),
    staleTime: 1000 * 60 * 5,
  });

  const { data: arAgingData } = useQuery({
    queryKey: QUERY_KEYS.accounting.arAging(),
    queryFn: () => getAccountsReceivableAging(),
    staleTime: 1000 * 60 * 5,
  });

  const { data: profitLossData } = useQuery({
    queryKey: QUERY_KEYS.accounting.profitAndLoss(),
    queryFn: () => getProfitAndLoss(),
    staleTime: 1000 * 60 * 5,
  });

  const [loading, setLoading] = useState(true);
  const { isCollapsed } = useSidebar();

  const zatcaStatusData = useMemo(() => {
    const deviceList = getList(devices, ["devices"]);

    const activeDevices = deviceList.filter(
      (device) =>
        device?.isActive !== false &&
        device?.status !== "INACTIVE" &&
        device?.status !== "DISABLED",
    );

    const devicesWithCertificates = activeDevices.filter((device) =>
      getDeviceCertificateExpiry(device),
    );

    const nearestExpiry = devicesWithCertificates
      .map(getDeviceCertificateExpiry)
      .filter(Boolean)
      .sort((a, b) => a.getTime() - b.getTime())[0];

    const daysLeft = getDaysLeft(nearestExpiry);

    const certificateStatus =
      daysLeft === null
        ? "unknown"
        : daysLeft < 0
          ? "error"
          : daysLeft <= 30
            ? "warning"
            : "active";

    const certificateDescription =
      daysLeft === null
        ? "Certificate expiry is not available from backend"
        : daysLeft < 0
          ? `Expired ${Math.abs(daysLeft)} days ago`
          : `Expires in ${daysLeft} days`;

    const phase1Active = activeDevices.length > 0;
    const phase2Active = activeDevices.some(
      (device) =>
        device?.phase === "P2" ||
        device?.zatcaPhase === "P2" ||
        device?.environment === "production" ||
        device?.productionCSID ||
        device?.pcsid ||
        device?.certificate ||
        device?.productionCertificate,
    );

    return {
      activeDeviceCount: activeDevices.length,
      certificateCount: devicesWithCertificates.length,
      nearestExpiry,
      daysLeft,
      zatcaItems: [
        {
          label: "Phase 1 - Generation",
          status: phase1Active ? "active" : "warning",
          progress: phase1Active ? 100 : 0,
          description: phase1Active
            ? `${activeDevices.length} active device${activeDevices.length === 1 ? "" : "s"} configured`
            : "No active ZATCA device configured",
        },
        {
          label: "Phase 2 - Real-time",
          status: phase2Active ? "active" : "warning",
          progress: phase2Active ? 100 : 0,
          description: phase2Active
            ? "Phase 2 device/certificate detected"
            : "Phase 2 production setup not detected",
        },
      ],
      certificateItems: [
        {
          label: "Production Certificate",
          status: certificateStatus,
          daysLeft,
          description: certificateDescription,
        },
        {
          label: "Configured Devices",
          status: activeDevices.length > 0 ? "active" : "warning",
          description:
            activeDevices.length > 0
              ? `${activeDevices.length} active device${activeDevices.length === 1 ? "" : "s"}`
              : "No active devices found",
        },
      ],
    };
  }, [devices]);

  const dashboardData = useMemo(() => {
    const invoiceList = getList(invoices, ["invoices"]);
    const payments = paymentsData?.payments || [];
    const clients = clientsData?.clients || clientsData?.data?.clients || [];
    const refunds = refundsData?.refunds || [];

    const todayInvoices = invoiceList.filter((invoice) =>
      isSameDay(getInvoiceActivityDate(invoice)),
    );

    const issuedInvoices = invoiceList.filter((invoice) =>
      ["ISSUED", "REPORTED", "CLEARED"].includes(invoice.status),
    );

    const draftInvoices = invoiceList.filter(
      (invoice) => invoice.status === "DRAFT",
    );

    const rejectedInvoices = invoiceList.filter(
      (invoice) => getDashboardInvoiceStatus(invoice) === "REJECTED",
    );

    const clearedInvoices = invoiceList.filter(
      (invoice) =>
        getDashboardInvoiceStatus(invoice) === "CLEARED" ||
        invoice.clearanceStatus === "CLEARED",
    );

    const pendingInvoices = invoiceList.filter(
      (invoice) => getDashboardInvoiceStatus(invoice) === "PENDING",
    );

    const salesRevenue =
      profitLossData?.summary?.netRevenue ??
      issuedInvoices.reduce(
        (sum, invoice) => sum + getInvoiceAmount(invoice),
        0,
      );

    const cashReceived = payments.reduce(
      (sum, payment) => sum + Number(payment.amount || 0),
      0,
    );

    const refundedAmount = refunds
      .filter((refund) => refund.status !== "VOID")
      .reduce((sum, refund) => sum + Number(refund.amount || 0), 0);

    const arSummary = arAgingData?.summary || {};

    const outstandingReceivables =
      arSummary.totalReceivable ??
      invoiceList.reduce((sum, invoice) => {
        if (["PAID", "CREDITED", "VOID"].includes(invoice.paymentStatus)) {
          return sum;
        }

        return sum + Number(invoice.balanceDue || 0);
      }, 0);

    const overdueReceivables =
      Number(arSummary.days1To30 || 0) +
      Number(arSummary.days31To60 || 0) +
      Number(arSummary.days61To90 || 0) +
      Number(arSummary.over90 || 0);

    const clearanceSuccessRate =
      issuedInvoices.length > 0
        ? (clearedInvoices.length / issuedInvoices.length) * 100
        : 0;

    const monthlyMap = new Map();

    invoiceList.forEach((invoice) => {
      const date = getInvoiceActivityDate(invoice);
      if (!date) return;

      const key = monthKey(date);
      const existing = monthlyMap.get(key) || {
        name: key,
        invoices: 0,
        cleared: 0,
        failed: 0,
      };

      existing.invoices += 1;

      if (
        getDashboardInvoiceStatus(invoice) === "CLEARED" ||
        invoice.clearanceStatus === "CLEARED"
      ) {
        existing.cleared += 1;
      }

      if (getDashboardInvoiceStatus(invoice) === "REJECTED") {
        existing.failed += 1;
      }

      monthlyMap.set(key, existing);
    });

    const invoiceVolume = Array.from(monthlyMap.values()).slice(-6);

    const hasInvoiceVolumeData = invoiceVolume.some(
      (row) => Number(row.invoices || 0) > 0,
    );

    const recentActivity = [
      ...invoiceList.slice(0, 5).map((invoice) => ({
        action: `${invoice.invoiceType || "Invoice"} ${getInvoiceStatus(invoice)}`,
        entity: invoice.invoiceNumber || invoice.uuid,
        time: getInvoiceActivityDate(invoice),
        type:
          invoice.status === "REJECTED"
            ? "warning"
            : getDashboardInvoiceStatus(invoice) === "CLEARED"
              ? "success"
              : "info",
      })),
      ...payments.slice(0, 5).map((payment) => ({
        action: "Payment recorded",
        entity: `${payment.clientName || "Client"} · SR ${money(payment.amount)}`,
        time: payment.paymentDate || payment.createdAt,
        type: "success",
      })),
      ...refunds.slice(0, 5).map((refund) => ({
        action: refund.status === "VOID" ? "Refund voided" : "Refund recorded",
        entity: `${refund.clientName || "Client"} · SR ${money(refund.amount)}`,
        time: refund.refundDate || refund.createdAt,
        type: refund.status === "VOID" ? "warning" : "info",
      })),
    ]
      .sort((a, b) => new Date(getRecentDate(b)) - new Date(getRecentDate(a)))
      .slice(0, 6);

    return {
      invoiceList,
      payments,
      clients,
      refunds,
      todayInvoices,
      issuedInvoices,
      draftInvoices,
      rejectedInvoices,
      clearedInvoices,
      pendingInvoices,
      salesRevenue,
      cashReceived,
      refundedAmount,
      outstandingReceivables,
      overdueReceivables,
      clearanceSuccessRate,
      invoiceVolume,
      hasInvoiceVolumeData,
      recentActivity,
      complianceData: [
        {
          name: t("status.cleared"),
          value: clearedInvoices.length,
          color: "#10B981",
        },
        {
          name: t("status.pending"),
          value: pendingInvoices.length,
          color: "#F59E0B",
        },
        {
          name: t("status.rejected"),
          value: rejectedInvoices.length,
          color: "#EF4444",
        },
        {
          name: t("status.draft"),
          value: draftInvoices.length,
          color: "#6B7280",
        },
      ].filter((item) => item.value > 0),
    };
  }, [
    invoices,
    paymentsData,
    clientsData,
    refundsData,
    arAgingData,
    profitLossData,
    t,
  ]);

  // Handle welcome message from company onboarding redirect
  useEffect(() => {
    if (location.state?.message) {
      const { message, type = "success" } = location.state;

      // Show welcome toast
      if (type === "success") {
        toast.success(message, {
          duration: 4000,
          position: "top-center",
        });
      } else {
        toast(message, {
          duration: 4000,
          position: "top-center",
        });
      }

      // Clear the state to prevent showing the message again
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return <LoadingContent isCollapsed={isCollapsed} />;
  }

  return (
    <div
      className={`transition-all duration-300 ease-in-out ${
        isCollapsed ? "md:ms-16" : "md:ms-64"
      }`}
    >
      <main className="p-4 md:p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {t("dashboard.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("dashboard.description")}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="md"
                iconName="Plus"
                onClick={() => navigate(ROUTES.INVOICE_CREATE)}
              >
                {t("actions.createInvoice")}
              </Button>

              <Button
                variant="default"
                size="md"
                iconName="Upload"
                onClick={() => navigate(ROUTES.INVOICE_UPLOAD)}
              >
                {t("actions.uploadInvoice")}
              </Button>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <KPICard
            title={t("dashboard.todaysInvoices")}
            value={dashboardData.todayInvoices.length}
            change={t("dashboard.issuedTotal", {
              count: dashboardData.issuedInvoices.length,
            })}
            trend="up"
            icon="FileText"
            color="primary"
          />

          <KPICard
            title={t("dashboard.salesRevenue")}
            value={`SR ${money(dashboardData.salesRevenue)}`}
            change={t("dashboard.issuedInvoices")}
            trend="up"
            icon="DollarSign"
            color="accent"
          />

          <KPICard
            title={t("dashboard.outstandingAR")}
            value={`SR ${money(dashboardData.outstandingReceivables)}`}
            change={t("dashboard.overdueAmount", {
              amount: money(dashboardData.overdueReceivables),
            })}
            trend={dashboardData.overdueReceivables > 0 ? "down" : "up"}
            icon="AlertTriangle"
            color={dashboardData.overdueReceivables > 0 ? "warning" : "success"}
          />

          <KPICard
            title={t("dashboard.cashReceived")}
            value={`SR ${money(dashboardData.cashReceived)}`}
            change={t("dashboard.refundedAmount", {
              amount: money(dashboardData.refundedAmount),
            })}
            trend="up"
            icon="Wallet"
            color="success"
          />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <ChartCard
              title={t("dashboard.salesInvoiceVolume")}
              subtitle={t("dashboard.salesInvoiceVolumeSubtitle")}
            >
              {dashboardData.hasInvoiceVolumeData ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={dashboardData.invoiceVolume}>
                    <defs>
                      <linearGradient
                        id="colorInvoices"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#3B82F6"
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor="#3B82F6"
                          stopOpacity={0.1}
                        />
                      </linearGradient>

                      <linearGradient
                        id="colorCleared"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#10B981"
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor="#10B981"
                          stopOpacity={0.1}
                        />
                      </linearGradient>

                      <linearGradient
                        id="colorFailed"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#EF4444"
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor="#EF4444"
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    </defs>

                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="name" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />

                    <Area
                      type="monotone"
                      dataKey="invoices"
                      stroke="#3B82F6"
                      fill="url(#colorInvoices)"
                      name="Total invoices"
                    />

                    <Area
                      type="monotone"
                      dataKey="cleared"
                      stroke="#10B981"
                      fill="url(#colorCleared)"
                      name="Cleared"
                    />

                    <Area
                      type="monotone"
                      dataKey="failed"
                      stroke="#EF4444"
                      fill="url(#colorFailed)"
                      name="Failed"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex flex-col items-center justify-center text-center rounded-xl border border-dashed border-border bg-muted/20">
                  <Icon
                    name="FileText"
                    size={36}
                    className="text-muted-foreground mb-3"
                  />
                  <h4 className="font-semibold text-foreground mb-1">
                    {t("dashboard.activityEmptyTitle")}
                  </h4>
                  <p className="text-sm text-muted-foreground max-w-sm">
                    {t("dashboard.activityEmptyDescription")}
                  </p>
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Plus"
                    className="mt-4"
                    onClick={() => navigate(ROUTES.INVOICE_CREATE)}
                  >
                    {t("actions.createInvoice")}
                  </Button>
                </div>
              )}
            </ChartCard>

            <ChartCard
              title={t("dashboard.invoiceStatusDistribution")}
              subtitle={t("dashboard.invoiceStatusSubtitle")}
            >
              {dashboardData.complianceData.length > 0 ? (
                <>
                  <div className="flex items-center justify-center">
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={dashboardData.complianceData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {dashboardData.complianceData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => `${value} invoices`} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="flex justify-center gap-6 mt-4">
                    {dashboardData.complianceData.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-sm text-muted-foreground">
                          {item.name}: {item.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-[250px] flex flex-col items-center justify-center text-center rounded-xl border border-dashed border-border bg-muted/20">
                  <Icon
                    name="PieChart"
                    size={36}
                    className="text-muted-foreground mb-3"
                  />
                  <h4 className="font-semibold text-foreground mb-1">
                    {t("dashboard.noInvoiceStatusesTitle")}
                  </h4>
                  <p className="text-sm text-muted-foreground max-w-sm">
                    {t("dashboard.noInvoiceStatusesDescription")}
                  </p>
                </div>
              )}
            </ChartCard>
          </div>

          <div className="space-y-6">
            <StatusCard
              title={t("dashboard.zatcaComplianceStatus")}
              items={zatcaStatusData.zatcaItems}
            />

            <StatusCard
              title={t("dashboard.certificateManagement")}
              items={zatcaStatusData.certificateItems}
            />
            <QuickActionsCard navigate={navigate} />
            <ActivityCard activities={dashboardData.recentActivity} />
          </div>
        </div>
      </main>
    </div>
  );
};

const DashboardOverview = () => {
  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background">
        <Sidebar />
        <DashboardContent />
      </div>
    </SidebarProvider>
  );
};

const KPICard = ({ title, value, change, trend, icon, color }) => {
  const colorClasses = {
    primary: "bg-primary/10 text-primary",
    success:
      "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400",
    warning:
      "bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400",
    accent: "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400",
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
      className="bg-card rounded-xl p-6 shadow-sm border border-border"
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon name={icon} size={24} />
        </div>
        <div
          className={`flex items-center text-sm ${
            trend === "up" ? "text-green-600" : "text-red-600"
          }`}
        >
          <Icon
            name={trend === "up" ? "TrendingUp" : "TrendingDown"}
            size={16}
          />
          <span className="ms-1">{change}</span>
        </div>
      </div>
      <h3 className="text-2xl font-bold text-foreground mb-1">{value}</h3>
      <p className="text-muted-foreground text-sm">{title}</p>
    </motion.div>
  );
};

const ChartCard = ({ title, subtitle, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: 0.2 }}
    className="bg-card rounded-xl p-6 shadow-sm border border-border"
  >
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-foreground mb-1">{title}</h3>
      <p className="text-muted-foreground text-sm">{subtitle}</p>
    </div>
    {children}
  </motion.div>
);

const StatusCard = ({ title, items }) => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.6, delay: 0.3 }}
    className="bg-card rounded-xl p-6 shadow-sm border border-border"
  >
    <h3 className="text-lg font-semibold text-foreground mb-4">{title}</h3>
    <div className="space-y-4">
      {items.map((item, index) => (
        <div key={index} className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">
              {item.label}
            </span>
            <StatusBadge status={item.status} daysLeft={item.daysLeft} />
          </div>
          {item.progress && (
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-green-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${item.progress}%` }}
              />
            </div>
          )}
          <p className="text-xs text-muted-foreground">{item.description}</p>
        </div>
      ))}
    </div>
  </motion.div>
);

const StatusBadge = ({ status, daysLeft }) => {
  const statusConfig = {
    active: {
      color:
        "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400",
      text:
        typeof daysLeft === "number" && daysLeft >= 0
          ? `${daysLeft} days`
          : "Active",
    },
    warning: {
      color:
        "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400",
      text:
        typeof daysLeft === "number" && daysLeft >= 0
          ? `${daysLeft} days`
          : "Needs setup",
    },
    error: {
      color: "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400",
      text: "Expired",
    },
    unknown: {
      color:
        "bg-muted text-muted-foreground dark:bg-muted dark:text-muted-foreground",
      text: "Unknown",
    },
  };

  const config = statusConfig[status] || statusConfig.unknown;

  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
    >
      {config.text}
    </span>
  );
};

const QuickActionsCard = ({ navigate }) => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.6, delay: 0.4 }}
    className="bg-card rounded-xl p-6 shadow-sm border border-border"
  >
    <h3 className="text-lg font-semibold text-foreground mb-4">
      Quick Actions
    </h3>

    <div className="space-y-3">
      <Button
        variant="ghost"
        size="sm"
        fullWidth
        iconName="Plus"
        className="justify-start"
        onClick={() => navigate(ROUTES.INVOICE_CREATE)}
      >
        Create New Invoice
      </Button>

      <Button
        variant="ghost"
        size="sm"
        fullWidth
        iconName="Wallet"
        className="justify-start"
        onClick={() => navigate(ROUTES.PAYMENT_MANAGEMENT)}
      >
        Record Payment
      </Button>

      <Button
        variant="ghost"
        size="sm"
        fullWidth
        iconName="RotateCcw"
        className="justify-start"
        onClick={() => navigate(ROUTES.REFUNDS)}
      >
        Refunds & Credit Notes
      </Button>

      <Button
        variant="ghost"
        size="sm"
        fullWidth
        iconName="Scale"
        className="justify-start"
        onClick={() => navigate(ROUTES.ACCOUNTING)}
      >
        Accounting Reports
      </Button>
    </div>
  </motion.div>
);

const ActivityCard = ({ activities = [] }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, delay: 0.5 }}
      className="bg-card rounded-xl p-6 shadow-sm border border-border"
    >
      <h3 className="text-lg font-semibold text-foreground mb-4">
        Recent Sales Activity
      </h3>

      {activities.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          No recent sales activity yet.
        </div>
      ) : (
        <div className="space-y-3">
          {activities.map((activity, index) => (
            <div key={index} className="flex items-start gap-3">
              <div
                className={`w-2 h-2 rounded-full mt-2 ${
                  activity.type === "success"
                    ? "bg-green-500"
                    : activity.type === "warning"
                      ? "bg-yellow-500"
                      : "bg-blue-500"
                }`}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground">
                  <span className="font-medium">{activity.action}</span>
                  <span className="text-muted-foreground ms-1">
                    {activity.entity}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {activity.time
                    ? new Date(activity.time).toLocaleString("en-SA")
                    : "—"}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

const LoadingContent = ({ isCollapsed }) => (
  <div
    className={`transition-all duration-300 ease-in-out ${
      isCollapsed ? "md:ms-16" : "md:ms-64"
    }`}
  >
    <main className="p-4 md:p-6">
      <div className="animate-pulse">
        <div className="mb-8">
          <div className="h-8 bg-muted rounded w-64 mb-2"></div>
          <div className="h-4 bg-muted rounded w-96"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="bg-card rounded-xl p-6 shadow-sm border border-border"
            >
              <div className="h-12 bg-muted rounded mb-4"></div>
              <div className="h-6 bg-muted rounded mb-2"></div>
              <div className="h-4 bg-muted rounded w-24"></div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {[...Array(2)].map((_, i) => (
              <div
                key={i}
                className="bg-card rounded-xl p-6 shadow-sm border border-border"
              >
                <div className="h-6 bg-muted rounded w-48 mb-4"></div>
                <div className="h-64 bg-muted rounded"></div>
              </div>
            ))}
          </div>
          <div className="space-y-6">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="bg-card rounded-xl p-6 shadow-sm border border-border"
              >
                <div className="h-6 bg-muted rounded w-32 mb-4"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-muted rounded"></div>
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-4 bg-muted rounded w-2/3"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  </div>
);

const LoadingState = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <LoadingContent isCollapsed={false} />
    </div>
  </SidebarProvider>
);

export default DashboardOverview;
